/**
 * 零依赖输入校验（领域层自带，保证任何运行时都能跑）。
 * 若某层想用 zod，可在该层再做一次校验，但**不得**放宽这里的规则。
 */

export interface ValidationIssue {
  field: string;
  message: string;
}

export interface ValidationResult<T> {
  ok: boolean;
  value?: T;
  issues: ValidationIssue[];
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function fail<T>(issues: ValidationIssue[]): ValidationResult<T> {
  return { ok: false, issues };
}

export interface RegisterInput {
  email: string;
  password: string;
  name: string;
  school: string;
}

export function validateRegister(input: unknown): ValidationResult<RegisterInput> {
  const issues: ValidationIssue[] = [];
  const v = (input ?? {}) as Record<string, unknown>;
  const email = typeof v.email === 'string' ? v.email.trim().toLowerCase() : '';
  const password = typeof v.password === 'string' ? v.password : '';
  const name = typeof v.name === 'string' ? v.name.trim() : '';
  const school = typeof v.school === 'string' ? v.school.trim() : '';

  if (!email) issues.push({ field: 'email', message: '邮箱必填' });
  else if (!EMAIL_RE.test(email)) issues.push({ field: 'email', message: '邮箱格式不正确' });
  if (password.length < 8) issues.push({ field: 'password', message: '密码至少 8 位' });
  if (password.length > 72) issues.push({ field: 'password', message: '密码过长（上限 72 位）' });
  if (!name) issues.push({ field: 'name', message: '姓名/昵称必填' });
  else if (name.length > 24) issues.push({ field: 'name', message: '姓名过长（上限 24 字）' });
  if (!school) issues.push({ field: 'school', message: '学校必填' });

  if (issues.length) return fail<RegisterInput>(issues);
  return { ok: true, value: { email, password, name, school }, issues: [] };
}

export interface LoginInput {
  email: string;
  password: string;
}

export function validateLogin(input: unknown): ValidationResult<LoginInput> {
  const issues: ValidationIssue[] = [];
  const v = (input ?? {}) as Record<string, unknown>;
  const email = typeof v.email === 'string' ? v.email.trim().toLowerCase() : '';
  const password = typeof v.password === 'string' ? v.password : '';
  if (!EMAIL_RE.test(email)) issues.push({ field: 'email', message: '邮箱格式不正确' });
  if (!password) issues.push({ field: 'password', message: '密码必填' });
  if (issues.length) return fail<LoginInput>(issues);
  return { ok: true, value: { email, password }, issues: [] };
}

export interface ProfileInput {
  school: string;
  major?: string;
  grade?: string;
  skills: string[];
  weeklyHours: number;
  slots?: string[];
  strengths?: string[];
  weaknesses?: string[];
  headline?: string;
}

export function validateProfile(input: unknown): ValidationResult<ProfileInput> {
  const issues: ValidationIssue[] = [];
  const v = (input ?? {}) as Record<string, unknown>;
  const skills = Array.isArray(v.skills) ? v.skills.filter((s): s is string => typeof s === 'string') : [];
  const weeklyHours = typeof v.weeklyHours === 'number' ? v.weeklyHours : Number.NaN;
  const school = typeof v.school === 'string' ? v.school.trim() : '';

  if (!school) issues.push({ field: 'school', message: '学校必填' });
  if (skills.length === 0) issues.push({ field: 'skills', message: '至少选择 1 个技能' });
  if (skills.length > 20) issues.push({ field: 'skills', message: '技能最多 20 个' });
  if (Number.isNaN(weeklyHours)) issues.push({ field: 'weeklyHours', message: '每周可投入时间必填' });
  else if (weeklyHours < 0 || weeklyHours > 60) issues.push({ field: 'weeklyHours', message: '每周可投入时间需在 0–60 小时之间' });

  if (issues.length) return fail<ProfileInput>(issues);
  const out: ProfileInput = { school, skills, weeklyHours };
  if (typeof v.major === 'string') out.major = v.major;
  if (typeof v.grade === 'string') out.grade = v.grade;
  if (Array.isArray(v.slots)) out.slots = v.slots.filter((s): s is string => typeof s === 'string');
  if (Array.isArray(v.strengths)) out.strengths = v.strengths.filter((s): s is string => typeof s === 'string');
  if (Array.isArray(v.weaknesses)) out.weaknesses = v.weaknesses.filter((s): s is string => typeof s === 'string');
  if (typeof v.headline === 'string') out.headline = v.headline.slice(0, 200);
  return { ok: true, value: out, issues: [] };
}

export interface MatchInput {
  requiredSkills?: string[];
  teamSkills?: string[];
  weeklyHoursNeeded?: number;
  eventCode?: string;
  allowCrossSchool?: boolean;
  limit?: number;
}

export function validateMatch(input: unknown): ValidationResult<MatchInput> {
  const issues: ValidationIssue[] = [];
  const v = (input ?? {}) as Record<string, unknown>;
  const requiredSkills = Array.isArray(v.requiredSkills)
    ? v.requiredSkills.filter((s): s is string => typeof s === 'string')
    : [];
  const eventCodeRaw = typeof v.eventCode === 'string' ? v.eventCode.trim() : '';
  // 选了赛事码时允许不手填技能——由该赛事已配置的需求驱动匹配（见 matching.service.ts runMatch）
  if (requiredSkills.length === 0 && eventCodeRaw.length === 0) {
    issues.push({
      field: 'requiredSkills',
      message: '请先填写项目需要的技能，或选择一个已配置需求的赛事码（留空则使用赛事需求）',
    });
  }
  if (requiredSkills.length > 30) issues.push({ field: 'requiredSkills', message: '需求技能最多 30 个' });
  if (issues.length) return fail<MatchInput>(issues);

  const out: MatchInput = { requiredSkills };
  if (Array.isArray(v.teamSkills)) out.teamSkills = v.teamSkills.filter((s): s is string => typeof s === 'string');
  if (typeof v.weeklyHoursNeeded === 'number') out.weeklyHoursNeeded = v.weeklyHoursNeeded;
  if (typeof v.eventCode === 'string') out.eventCode = v.eventCode;
  if (typeof v.allowCrossSchool === 'boolean') out.allowCrossSchool = v.allowCrossSchool;
  if (typeof v.limit === 'number') out.limit = v.limit;
  return { ok: true, value: out, issues: [] };
}

/** 赛事码：6–12 位大写字母数字，便于口头传播 */
export function validateEventCode(code: unknown): ValidationResult<string> {
  if (typeof code !== 'string' || !/^[A-Z0-9]{6,12}$/.test(code.trim().toUpperCase())) {
    return fail<string>([{ field: 'eventCode', message: '赛事码为 6–12 位大写字母或数字' }]);
  }
  return { ok: true, value: code.trim().toUpperCase(), issues: [] };
}